import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Menu, Dropdown } from "antd";
import {
    EllipsisOutlined,
    CloudDownloadOutlined,
    SettingOutlined,
    FileZipOutlined,
} from "@ant-design/icons";
import ManageFilesModal from "../manage-files-modal";
import ArticleGalaxyMenuItem from "@app/components/article-galaxy/menu-item";

import UploadMenuItem from "@app/components/form/upload/menu-item";
import file from "@app/lib/file";
import state from "@app/state/store/report/article";

import articleGalaxyUtils from "../utils/article-galaxy";

import "./style/menu.scoped.scss";
import classNames from "classnames";
import format from "@app/lib/format";

const ListActions = observer(
    ({
        article = {},
        onUpload,
        className,
        isL2 = false,
        projectId,
        readonly = false,
        ...props
    }) => {
        const [manageFilesVisible, setManageFilesVisible] = useState(false);

        const handleRenameFile = async (fileId, newName) => {
            await state.renameSupplementaryFile(article._id, fileId, newName);
        };

        const handleDeleteFile = async (fileId) => {
            await state.removeSupplementaryFile(article._id, fileId);
        };

        const handleArticleGalaxyAction = async () => {
            articleGalaxyUtils.order(article);
        };

        const handleAddSupplementaryFile = async (file) => {
            await state.addSupplementaryFile(article._id, file._id);
        };

        const onAction = ({ key }) => {
            if (key === "download") {
                file.download(
                    article.file,
                    format.getFileName(article, {
                        _id: article.file,
                        name: article.title,
                        isMain: true,
                    }),
                );
            } else if (key === "export-all") {
                file.exportArticleWithFiles(article._id, article.project);
            }
        };

        if (readonly && !article.file) {
            return null;
        }

        const menu = (
            <Menu onClick={onAction} className="menu">
                {!readonly && (
                    <Menu.Item key="upload">
                        <UploadMenuItem
                            accept=".pdf,application/pdf"
                            onChange={(file) => {
                                onUpload(file);
                            }}
                        >
                            Upload Full Text
                        </UploadMenuItem>
                    </Menu.Item>
                )}
                {!readonly && article.file && (
                    <Menu.Item key="upload-supplementary">
                        <UploadMenuItem
                            accept=".pdf,application/pdf"
                            onChange={handleAddSupplementaryFile}
                        >
                            Upload Supplementary File
                        </UploadMenuItem>
                    </Menu.Item>
                )}
                {article.file && (
                    <Menu.Item key="download" icon={<CloudDownloadOutlined size={16} />}>
                        Download Full Text
                    </Menu.Item>
                )}
                {!readonly && isL2 && article.file && article.supplementaryFiles?.length > 0 && (
                    <Menu.Item key="export-all" icon={<FileZipOutlined size={16} />}>
                        Export All Files
                    </Menu.Item>
                )}
                {!readonly && isL2 && article.file && article.supplementaryFiles?.length > 0 && (
                    <Menu.Item
                        key="manage-files"
                        icon={<SettingOutlined size={16} />}
                        onClick={() => setManageFilesVisible(true)}
                    >
                        Manage Uploaded Files
                    </Menu.Item>
                )}
                {manageFilesVisible && (
                    <ManageFilesModal
                        article={article}
                        visible={manageFilesVisible}
                        onCancel={() => setManageFilesVisible(false)}
                        onRenameFile={handleRenameFile}
                        onDeleteFile={handleDeleteFile}
                    />
                )}
                {!readonly && articleGalaxyUtils.isOrderEnabled() && <Menu.Divider />}
                {!readonly && articleGalaxyUtils.isOrderEnabled() && (
                    <Menu.Item
                        key="article-galaxy"
                        onClick={handleArticleGalaxyAction}
                        disabled={!articleGalaxyUtils.canOrder(article)}
                    >
                        <ArticleGalaxyMenuItem article={article} />
                    </Menu.Item>
                )}
            </Menu>
        );

        return (
            <div className={classNames("card-menu", className)} {...props}>
                <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown overlay={menu} trigger="click" placement="bottomRight">
                        <Button type="icon" icon={<EllipsisOutlined />} />
                    </Dropdown>
                </div>
            </div>
        );
    },
);

export default ListActions;
